import { Component, ElementRef, ViewChild, ViewContainerRef, TemplateRef, AfterViewInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Overlay, OverlayRef, OverlayPositionBuilder } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DialogService, GeneralService } from '../../services';
import { Messages } from 'src/app/constants/messages';
import { ButtonColor, ButtonType } from 'src/app/model';
import { ServicecallService } from 'src/app/services';

@Component({
  selector: 'app-virtual-scroll-search-dropdown',
  templateUrl: './virtual-scroll-search-dropdown.component.html',
  styleUrl: './virtual-scroll-search-dropdown.component.css'
})
export class VirtualScrollSearchDropdownComponent implements AfterViewInit, OnDestroy {

  @ViewChild('trigger') trigger: ElementRef;
  @ViewChild('dropdownTemplate') dropdownTemplate: TemplateRef<any>;
  @ViewChild('searchInput') searchInput: ElementRef;

  @Input() validators: { [key: string]: boolean };
  @Input() formSubmit: boolean = false;
  @Input() options: any[] = [];
  @Input() formLabel: string;
  @Input() selectedOption: any;
  @Input() required: boolean = false;
  @Input() addOption: boolean = false;
  @Input()
  set isLoader(value: boolean) {
    this._isLoader = value;
  }

  get isLoader() {
    return this._isLoader;
  }

  @Output() valueChange = new EventEmitter<any>();
  @Output() optionAdditionOrDeletion = new EventEmitter<any>();

  filteredOptions: any[] = [];
  searchControl = new FormControl('');
  optionLabel = new FormControl('', Validators?.required);
  showOptions: boolean = false;
  optionValue = new FormControl('', Validators?.required);

  private _overlayRef: OverlayRef;
  private _portal: TemplatePortal;
  private _searchSub: Subscription;
  private _isLoader: boolean;
  private _formActionDialogSubscription: Subscription;

  constructor(
    private _overlay: Overlay,
    private _vcr: ViewContainerRef,
    private _positionBuilder: OverlayPositionBuilder,
    public generalService: GeneralService,
    private _dialogService: DialogService,
    public service: ServicecallService
  ) { }

  ngAfterViewInit() {
    const positionStrategy = this._positionBuilder
      .flexibleConnectedTo(this.trigger)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
      ]);

    this._overlayRef = this._overlay?.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      positionStrategy,
      scrollStrategy: this._overlay?.scrollStrategies?.reposition(),
    });

    this._overlayRef?.backdropClick()?.subscribe(() => this.close());
    this._searchSub = this.searchControl?.valueChanges?.subscribe((value) => {
      const filter = (value || '')?.toLowerCase();
      this.filteredOptions = this.options?.filter(opt =>
        opt?.name?.toLowerCase()?.includes(filter)
      );
    });
  }

  ngOnDestroy() {
    this._overlayRef?.dispose();
    this._searchSub?.unsubscribe();
    this._formActionDialogSubscription?.unsubscribe();
  }

  /**
   * Opens virtual scroll search dropdown component
   */
  open() {
    if (!this._overlayRef?.hasAttached()) {
      this.filteredOptions = [...this.options];
      this.searchControl?.setValue('');
      this._portal = new TemplatePortal(this.dropdownTemplate, this._vcr);
      this._overlayRef?.attach(this._portal);

      setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    }
  }

  /**
   * Closes virtual scroll search dropdown component
   */
  close() {
    this._overlayRef?.detach();
    this.optionLabel.reset();
    this.optionValue.reset();
    this.showOptions = false;
  }

  /**
   * Selects virtual scroll search dropdown component
   * @param option 
   */
  select(option: any) {
    this.selectedOption = option;
    this.valueChange?.emit(option);
    this.close();
  }

  /**
   * Adds new option
   */
  addNewOption() {
    const newOption = {
      value: this.optionValue?.value,
      name: this.optionLabel?.value,
    };
    const exists = this.options.some(opt => opt?.value === newOption?.value);
    if (!exists) {
      this.options.push(newOption);
      this.filteredOptions = [...this.options];
      this.showOptions = !this.showOptions;
      this.optionLabel.reset();
      this.optionValue.reset();
      this.optionAdditionOrDeletion.emit(this.filteredOptions);
    }
  }
  
  /**
   * Values validation
   * @param event 
   */
  valueValidation(event: any) {
    const exists = this.options?.some(opt => opt?.value === event?.target?.value);
    if(exists) {
      this.optionValue?.setErrors({duplicateError: true});
    } else {
      this.optionValue?.setErrors(null);
    }
  }

  /**
   * Names validation
   * @param event 
   */
  nameValidation(event: any) {
    const exists = this.options?.some(opt => opt?.name === event?.target?.value);
    if(exists) {
      this.optionLabel?.setErrors({duplicateError: true});
    } else {
      this.optionLabel?.setErrors(null);
    }
  }

  /**
   * Deletes an option from the list
   */
  deleteOption(option: any) {
    let dialogConfig = {
      data: {
        title: Messages?.OPTION_DELETE_DIALOG_TITLE,
        content: Messages.DELETE_OPTION?.replace(
          '[OPTION_NAME]', option?.name
        ),
        actions: [
          {
            label: Messages?.BUTTON_LABEL_NO,
            type: ButtonType?.BUTTON
          },
          {
            label: Messages?.BUTTON_LABEL_YES,
            type: ButtonType?.BUTTON,
            value: true,
            color: ButtonColor.PRIMARY
          }
        ],
        showClose: true
      }
    };
    this._formActionDialogSubscription = this._dialogService
    ?.showDialog(dialogConfig)
    ?.afterClosed()
    ?.subscribe({
      next: (response: boolean) => {
        if (response) {
          this.options = this.options.filter(opt => opt.value !== option.value);
          this.filteredOptions = this.filteredOptions.filter(opt => opt.value !== option.value);
          this.optionAdditionOrDeletion.emit(this.filteredOptions);
          if (this.selectedOption?.value === option.value) {
            this.selectedOption = null;
            this.valueChange.emit(null);
          }
        }
      }
    });
  }
}
